import { useMutation } from '@tanstack/react-query';
import {
  fetchInstance,
  getEndpoint,
  gqlClient,
  graphql,
  type Tyrone_Prompt,
} from '@tyro/api';
import { useCallback, useRef, useState } from 'react';

const tyronePrompt = graphql(/* GraphQL */ `
  query tyrone_prompt($filter: Tyrone_Prompt!) {
    tyrone_prompt(filter: $filter) {
      conversationId
      result
    }
  }
`);

export function useTyronePrompt() {
  return useMutation({
    mutationFn: async (filter: Tyrone_Prompt) =>
      gqlClient.request(tyronePrompt, { filter }),
  });
}

export interface TyroneStreamUsage {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  inputCost: number;
  outputCost: number;
  totalCost: number;
}

export interface DocSource {
  title: string;
  slug: string;
}

export type TyroneStreamEvent =
  | { type: 'conversation'; conversationId: string }
  | { type: 'delta'; text: string }
  | { type: 'usage'; usage: TyroneStreamUsage }
  | { type: 'sources'; sources: DocSource[] }
  | { type: 'error'; message: string };

export interface TyronePageContext {
  timetableId?: number;
}

export interface TyroneStreamOptions {
  prompt: string;
  conversationId?: string;
  pageContext?: TyronePageContext;
  onEvent: (event: TyroneStreamEvent) => void;
  signal?: AbortSignal;
}

const streamPath = '/api/tyrone/chat/stream';

function getStreamUrl(): string {
  const base = new URL(getEndpoint());
  base.pathname = streamPath;
  base.search = '';
  return base.toString();
}

export async function streamTyronePrompt({
  prompt,
  conversationId,
  pageContext,
  onEvent,
  signal,
}: TyroneStreamOptions): Promise<void> {
  const response = await fetchInstance(getStreamUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({
      prompt,
      conversationId,
      pageTimetableId: pageContext?.timetableId,
    }),
    signal,
  });

  if (!response.ok || !response.body) {
    throw new Error(`Tyrone stream failed: HTTP ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        if (buffer.length > 0) {
          dispatchEvent(buffer, onEvent);
        }
        break;
      }
      buffer += decoder.decode(value, { stream: true });

      let separatorIdx = buffer.indexOf('\n\n');
      while (separatorIdx >= 0) {
        const rawEvent = buffer.slice(0, separatorIdx);
        buffer = buffer.slice(separatorIdx + 2);
        dispatchEvent(rawEvent, onEvent);
        separatorIdx = buffer.indexOf('\n\n');
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function dispatchEvent(
  rawEvent: string,
  onEvent: (event: TyroneStreamEvent) => void,
): void {
  const parsed = parseSseEvent(rawEvent);
  if (!parsed) return;
  const mapped = mapStreamEvent(parsed);
  if (mapped) onEvent(mapped);
}

function parseSseEvent(raw: string): { event: string; data: string } | null {
  let event = '';
  const dataLines: string[] = [];
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      // Spring's SseEmitter serialises as `data:<content>` without the optional
      // SSE protocol space, so any leading space in the value is real content
      // (e.g. the leading space of a streamed token). Don't strip it.
      dataLines.push(line.slice('data:'.length));
    }
  }
  if (!event) return null;
  return { event, data: dataLines.join('\n') };
}

function mapStreamEvent(raw: {
  event: string;
  data: string;
}): TyroneStreamEvent | null {
  const parseJson = <T,>(): T | null => {
    if (!raw.data) return null;
    try {
      return JSON.parse(raw.data) as T;
    } catch {
      return null;
    }
  };

  switch (raw.event) {
    case 'conversation': {
      const data = parseJson<{ conversationId: string }>();
      return data
        ? { type: 'conversation', conversationId: data.conversationId }
        : null;
    }
    case 'delta': {
      const data = parseJson<{ text: string }>();
      return data ? { type: 'delta', text: data.text } : null;
    }
    case 'usage': {
      const data = parseJson<TyroneStreamUsage>();
      return data ? { type: 'usage', usage: data } : null;
    }
    case 'sources': {
      const data = parseJson<DocSource[]>();
      return data && Array.isArray(data)
        ? { type: 'sources', sources: data }
        : null;
    }
    case 'error': {
      const data = parseJson<{ message: string }>();
      return {
        type: 'error',
        message: data?.message ?? 'Tyrone chat stream failed',
      };
    }
    case 'done':
      return null;
    default:
      return null;
  }
}

export interface TyroneStreamCallbacks {
  onConversation?: (conversationId: string) => void;
  onDelta: (text: string) => void;
  onUsage?: (usage: TyroneStreamUsage) => void;
  onSources?: (sources: DocSource[]) => void;
  onError?: (error: Error) => void;
  onDone?: () => void;
}

/**
 * Hook wrapper around {@link streamTyronePrompt}. Decomposes the tagged-union
 * event into individual callbacks and tracks pending state + cancellation.
 *
 * Use {@link streamTyronePrompt} directly when you need to dispatch events
 * centrally (e.g. into a reducer); use this hook when a component just wants
 * to wire delta / sources / usage into local state.
 */
export function useTyronePromptStream() {
  const [isPending, setIsPending] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const stream = useCallback(
    async (
      input: {
        prompt: string;
        conversationId?: string;
        pageContext?: TyronePageContext;
      },
      callbacks: TyroneStreamCallbacks,
    ) => {
      setIsPending(true);
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        await streamTyronePrompt({
          prompt: input.prompt,
          conversationId: input.conversationId,
          pageContext: input.pageContext,
          signal: controller.signal,
          onEvent: (event) => {
            switch (event.type) {
              case 'conversation':
                callbacks.onConversation?.(event.conversationId);
                break;
              case 'delta':
                callbacks.onDelta(event.text);
                break;
              case 'usage':
                callbacks.onUsage?.(event.usage);
                break;
              case 'sources':
                callbacks.onSources?.(event.sources);
                break;
              case 'error':
                callbacks.onError?.(new Error(event.message));
                break;
            }
          },
        });
        callbacks.onDone?.();
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          callbacks.onError?.(error as Error);
        }
      } finally {
        setIsPending(false);
        abortRef.current = null;
      }
    },
    [],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return { stream, cancel, isPending };
}
